import { replace } from "lodash";
import numeral from "numeral";

// ----------------------------------------------------------------------

export function fCurrency(number) {
  try {
    return numeral(number).format(
      Number.isInteger(number) ? "€0,0" : "€0,0.00"
    );
  } catch (error) {
    return "€0,0";
  }
}

export function fPercent(number) {
  return numeral(number / 100).format("0.0%");
}

export function fNumber(number) {
  return numeral(number).format();
}

export function fShortenNumber(number) {
  return replace(numeral(number).format("0.00a"), ".00", "");
}

export function fData(number) {
  return numeral(number).format("0.0 b");
}

export function fDecimals(number) {
  if (!number) return 0;

  return Number(number)?.toFixed(2) ?? number;
}

export function fCurrencyDecimals(number) {
  return fCurrency(fDecimals(number));
}

// Etiqueta de precio para la tarjeta de producto en la carta (privada
// y camarero). Un producto al peso NO tiene "precio unitario" — el
// importe se calcula al pesar. Antes se pintaba `0 €`, ahora mostramos
// `12,50 €/kg` para que el camarero/cliente vea el precio por kilo.
//
//   • priceByMarket → "S/M"
//   • ventaAlPeso + precioKg → "{precioKg} €/kg"
//   • resto → "{precio} €"
//
// Devuelve solo el texto (sin JSX) para que se pueda meter en
// cualquier <Typography> o <Label> respetando el estilo de cada
// vista. Ver [[project-weight-picker-redesign]] para el flujo de
// captura de peso.
export function formatCartaPriceLabel({
  precio,
  priceByMarket,
  ventaAlPeso,
  precioKg,
}) {
  if (priceByMarket) return "S/M";
  if (ventaAlPeso && Number(precioKg) > 0) {
    return `${fDecimals(precioKg)} €/kg`;
  }
  return `${fDecimals(precio)} €`;
}

// 2026-10-26 · Redondeo monetario ÚNICO (igual que el servidor: decimal con
// MidpointRounding.AwayFromZero): 2 decimales, mitad hacia arriba. `toFixed`
// redondea el valor binario (1.005.toFixed(2) === "1.00") y el TPV podía cobrar un
// céntimo distinto del que guardaba el servidor (arqueos que no cuadraban).
export function round2(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  const abs = Math.abs(n);
  if (abs < 0.000001) return 0;
  // toPrecision(15) quita el ruido binario (10.355000000000000426 → 10.355) y el
  // desplazamiento por exponente redondea sobre el decimal "de verdad".
  const clean = Number(abs.toPrecision(15));
  const rounded = Number(`${Math.round(Number(`${clean}e2`))}e-2`);
  return n < 0 ? -rounded : rounded;
}

export function CalculateDiscount(selectedDiscount, total) {
  if (!total) return Number(0);

  if (!selectedDiscount) return fDecimals(Number(total));

  // 2026-10-26 · En céntimos enteros y redondeo único mitad hacia arriba, igual
  // que el API (Discount.CalculateDiscount, decimal AwayFromZero): total =
  // round(bruto × (1 ∓ %/100), 2). Antes en coma flotante + toFixed: los medios
  // céntimos salían a veces hacia abajo (5,10 − 5 % → 4,84; 10,90 − 5 % → 10,36)
  // y el ticket, VeriFactu y el arqueo no cuadraban con lo cobrado.
  const grossCents = Math.round(Number(total) * 100);
  const sign = selectedDiscount.isSuplement ? 1 : -1;
  let resCents = grossCents;

  if (selectedDiscount?.percentage) {
    // Porcentaje en centésimas (12,5 % → 1250): numerador entero y exacto.
    const bp = Math.round(Number(selectedDiscount.percentage) * 100);
    resCents = Math.round((grossCents * (10000 + sign * bp)) / 10000);
  }

  if (selectedDiscount?.moneyQuantity)
    resCents =
      grossCents + sign * Math.round(Number(selectedDiscount.moneyQuantity) * 100);

  if (resCents < 0) return 0;

  return fDecimals(resCents / 100);
}
